import { useMutation } from "@apollo/client";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, SyntheticEvent } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type Crop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Info } from "@mui/icons-material";
import { Vendor } from "../../../../__generated__/graphql";
import { DELETE_VENDOR, UPDATE_VENDOR } from "../graphql";
import { VendorPicker } from "../../../../components/VendorTypePicker";
import MarkdownEditor from "../../../../components/MarkdownEditor";
import { SocialLinks, vendorDefaults } from "../../../post/SocialLinks";
import VendorLinksList from "../../../band/links/VendorLinkList";
import { Dish } from "../../dish";
import { ErrorRenderer } from "../../../../components/ErrorRenderer";
import { getUpdatedImageUrl } from "../../../post/edit/helpers";
import { TabbedPage } from "../../../../components/TabbedPage";
import { NavigationBlocker } from "../../../../components/NavigationBlocker";
import { ImagePreview } from "../../../../components/ImagePreview";

const schema = yup
  .object({
    name: yup.string().required(),
    description: yup.string().required(),
    vendorType: yup.string().required("vendor type is required"),
    image: yup.string(),
    socials: yup.object({
      facebook: yup.string().url().optional(),
      tiktok: yup.string().url().optional(),
      twitter: yup.string().url().optional(),
      instagram: yup.string().url().optional(),
      website: yup.string().url().optional(),
    }),
  })
  .required();

const VendorForm = ({ vendor }: { vendor: Vendor }) => {
  const navigate = useNavigate();
  const [updateVendor, { loading: updateLoading, error }] = useMutation(
    UPDATE_VENDOR,
    {
      onCompleted: () => {
        toast("Vendor updated");
      },
    }
  );
  const [deleteVendor] = useMutation(DELETE_VENDOR, {
    onCompleted: () => {
      toast("Vendor deleted");
      navigate("/vendors");
    },
  });
  const { id } = useParams();
  const [crop, setCrop] = useState<Crop>({
    unit: "%", // Can be 'px' or '%'
    x: 25,
    y: 25,
    width: 50,
    height: 50,
  });

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: vendor.name,
      description: vendor.description,
      vendorType: vendor.type,
      image: vendor.image,
      socials: vendor.socials ?? vendorDefaults,
    },
  });

  const handleDelete = () => {
    confirmAlert({
      title: "Delete this Vendor?",
      message: "Are you sure? this can't be undone",
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteVendor({ variables: { vendorId: vendor.id } }),
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const [imageUrl, setImageUrl] = useState(vendor.image ?? "");
  const [tabIndex, setTabIndex] = useState(0);

  const location = useLocation();

  useEffect(() => {
    if (location?.state?.updatedImageUrl) {
      setImageUrl(location?.state?.updatedImageUrl);
      setValue("image", location?.state?.updatedImageUrl, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [location.state]);

  const onSubmit = (val: any) => {
    const { name, description, vendorType, socials } = val;
    updateVendor({
      variables: {
        vendor: {
          id: vendor.id,
          name,
          description,
          image: imageUrl,
          type: vendorType,
          socials,
        },
      },
    });
  };
  // aspect ratio 3∶2

  const imageKey = location?.state?.updated ?? "imageUrl";

  const headerActions = (
    <Stack
      direction="row"
      spacing={1.5}
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
    >
      <Button type="submit" variant="contained" form="vendor-details-form">
        Save
      </Button>
      <Button variant="contained" color="error" onClick={handleDelete}>
        Delete Vendor
      </Button>
    </Stack>
  );

  const vendorDetailsContent = (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        id="vendor-details-form"
        sx={{ display: "contents" }}
      >
        <Stack spacing={3}>
          <Card elevation={1}>
            <CardContent>
              <Grid container spacing={4} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                  <Stack spacing={2}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                    >
                      Featured Image
                    </Typography>
                    <ImagePreview
                      key={imageKey}
                      src={getUpdatedImageUrl(imageUrl)}
                      alt="Vendor artwork"
                    />
                    <Button
                      variant="outlined"
                      onClick={() => {
                        navigate("/image", {
                          state: {
                            vendorId: vendor.id,
                          },
                        });
                      }}
                    >
                      Update Image
                    </Button>
                    <TextField
                      id="vendor-image"
                      label="Image URL"
                      variant="outlined"
                      defaultValue={vendor?.image}
                      {...register("image")}
                      onChange={(value) => {
                        setImageUrl(value.target.value);
                      }}
                      fullWidth
                      margin="dense"
                      error={Boolean(errors.image)}
                      helperText={errors.image?.message}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Stack spacing={3}>
                    <div>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          mb: 1,
                        }}
                      >
                        Vendor Details
                      </Typography>
                      <TextField
                        id="vendor-name"
                        label="Name"
                        variant="outlined"
                        defaultValue={vendor?.name}
                        {...register("name")}
                        fullWidth
                        margin="dense"
                        error={Boolean(errors.name)}
                        helperText={errors.name?.message}
                      />
                    </div>
                    <div>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          mb: 1,
                        }}
                      >
                        Description
                      </Typography>
                      <Controller
                        control={control}
                        {...register("description")}
                        render={({ field: { onChange, value } }) => (
                          <MarkdownEditor
                            text={value ?? vendor?.description ?? ""}
                            onChange={onChange}
                          />
                        )}
                      />
                      {errors.description?.message && (
                        <Typography variant="caption" color="error">
                          {errors.description?.message}
                        </Typography>
                      )}
                    </div>
                    <div>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          mb: 1,
                        }}
                      >
                        Vendor Type
                      </Typography>
                      <Controller
                        render={({ field }) => (
                          <VendorPicker
                            {...field}
                            defaultValue={vendor.type}
                            value={field.value}
                            handleChange={(newValue: string) =>
                              field.onChange(newValue)
                            }
                          />
                        )}
                        name={`vendorType`}
                        control={control}
                      />
                      {errors.vendorType?.message && (
                        <Typography variant="caption" color="error">
                          {errors.vendorType?.message}
                        </Typography>
                      )}
                    </div>
                    <div>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          mb: 1,
                        }}
                      >
                        Social Links
                      </Typography>
                      <Controller
                        control={control}
                        name="socials"
                        render={({ field: { onChange, value } }) => (
                          <SocialLinks
                            socialType="NEWS"
                            socialDefaults={vendorDefaults}
                            data={value}
                            // @ts-ignore
                            onChange={onChange}
                            errors={errors.socials}
                          />
                        )}
                      />
                    </div>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <ErrorRenderer errors={errors} />
        </Stack>
      </Box>
    </>
  );

  const linkedItemsContent = vendor ? (
    <Stack spacing={3}>
      <Card elevation={1}>
        <CardContent>
          <Stack spacing={2}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Linked Items
            </Typography>
            <VendorLinksList vendor={vendor} disableContainer />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  ) : (
    <Typography variant="body2" color="text.secondary">
      Save the vendor before managing linked items.
    </Typography>
  );

  const dishesContent = vendor ? (
    <Stack spacing={3}>
      <Card elevation={1}>
        <CardContent>
          <Stack spacing={2}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Dishes
            </Typography>
            <Dish vendor={vendor} />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  ) : (
    <Typography variant="body2" color="text.secondary">
      Save the vendor before editing dishes.
    </Typography>
  );

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  return (
    <>
      <NavigationBlocker isDirty={isDirty} />
      <TabbedPage
        title={`Edit Vendor: ${vendor.name}`}
        actions={headerActions}
        value={tabIndex}
        onChange={handleTabChange}
        tabs={[
          {
            label: "Vendor Details",
            id: "vendor-tab-0",
            ariaControlsId: "vendor-tabpanel-0",
            content: vendorDetailsContent,
          },
          {
            label: "Linked Items",
            id: "vendor-tab-1",
            ariaControlsId: "vendor-tabpanel-1",
            content: linkedItemsContent,
            disabled: !vendor,
          },
          {
            label: "Dishes",
            id: "vendor-tab-2",
            ariaControlsId: "vendor-tabpanel-2",
            content: dishesContent,
            disabled: !vendor,
          },
        ]}
      />
    </>
  );
};
export default VendorForm;
